import axios from 'axios';

const STUN = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }];

// Shared WebRTC helper: polls REST signaling. side = 'user' | 'admin'
export const createCallSession = ({ api, headers, side, onRemoteStream, onStatus, onInfo }) => {
  const state = { pc: null, callId: null, timer: null, seenIce: 0, local: null, ended: false, pendingLogs: [], pendingIce: [] };
  const base = () => (side === 'user' ? `${api}/m/calls/${state.callId}` : `${api}/calls/${state.callId}`);
  const signal = (type, data) => axios.post(`${base()}/signal`, { type, data }, { headers: headers() }).catch(() => {});
  const log = (msg) => {
    onInfo?.(msg);
    if (state.callId) signal('log', msg); else state.pendingLogs.push(msg);
  };
  const flushLogs = () => {
    state.pendingLogs.forEach(m => signal('log', m)); state.pendingLogs = [];
    log(`flush_ice=${state.pendingIce.length}`);
    state.pendingIce.forEach(c => signal('ice', c)); state.pendingIce = [];
  };
  const reportPair = async () => {
    try {
      const stats = await state.pc.getStats();
      let pair = null; const cands = {};
      stats.forEach(r => { if (r.type === 'local-candidate' || r.type === 'remote-candidate') cands[r.id] = r; });
      stats.forEach(r => { if (r.type === 'candidate-pair' && (r.selected || r.nominated) && r.state === 'succeeded') pair = r; });
      if (pair) log(`pair local=${cands[pair.localCandidateId]?.candidateType}/${cands[pair.localCandidateId]?.protocol} remote=${cands[pair.remoteCandidateId]?.candidateType}`);
      let audioIn = 0; stats.forEach(r => { if (r.type === 'inbound-rtp' && r.kind === 'audio') audioIn = r.packetsReceived; });
      log(`inbound_audio_packets=${audioIn}`);
    } catch (e) { log('stats_err ' + e.message); }
  };
  const init = async () => {
    let iceServers = STUN;
    try { const r = await axios.get(`${api}/m/calls/ice`); if (Array.isArray(r.data?.iceServers) && r.data.iceServers.length) iceServers = r.data.iceServers; } catch {}
    log(`ice_servers=${iceServers.length} ua=${navigator.userAgent.slice(0, 80)}`);
    const pc = new RTCPeerConnection({ iceServers });
    pc.onicecandidate = (e) => { if (!e.candidate) return; const c = e.candidate.toJSON(); if (state.callId) signal('ice', c); else state.pendingIce.push(c); };
    pc.ontrack = (e) => { log('track_received'); onRemoteStream?.(e.streams[0]); };
    pc.oniceconnectionstatechange = () => log('ice=' + pc.iceConnectionState);
    pc.onconnectionstatechange = () => {
      log('conn=' + pc.connectionState);
      if (pc.connectionState === 'connected') { onStatus?.('connected'); setTimeout(reportPair, 4000); }
      else if (pc.connectionState === 'disconnected') onStatus?.('reconnecting');
      else if (['failed', 'closed'].includes(pc.connectionState)) end(true);
    };
    try {
      state.local = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      log('mic_ok');
    } catch (e) { log('mic_err ' + e.name); throw e; }
    state.local.getTracks().forEach(t => pc.addTrack(t, state.local));
    try { if (navigator.audioSession) { navigator.audioSession.type = 'play-and-record'; log('audioSession=play-and-record'); } } catch {}
    state.pc = pc;
    return pc;
  };
  const drainIce = async (ice) => {
    const pc = state.pc;
    if (!pc || !pc.remoteDescription) return;
    for (; state.seenIce < ice.length; state.seenIce++) { try { await pc.addIceCandidate(ice[state.seenIce]); } catch (e) { log('ice_add_err ' + e.message); } }
  };
  const poll = async () => {
    if (state.ended || !state.callId) return;
    try {
      const r = await axios.get(base(), { headers: headers() });
      const c = r.data;
      if (side === 'user' && c.answer && state.pc.signalingState === 'have-local-offer') { await state.pc.setRemoteDescription(c.answer); log('answer_set'); onStatus?.('active'); }
      await drainIce(c.ice || []);
      if (['ended', 'missed'].includes(c.status)) end(false);
    } catch { }
  };
  const startPolling = () => { state.timer = setInterval(poll, 1500); };
  const end = (notify = true) => {
    if (state.ended) return; state.ended = true;
    clearInterval(state.timer);
    if (notify && state.callId) signal('hangup');
    state.local?.getTracks().forEach(t => t.stop());
    try { state.pc?.close(); } catch {}
    try { if (navigator.audioSession) { navigator.audioSession.type = 'playback'; navigator.audioSession.type = 'auto'; } } catch {}
    onStatus?.('ended');
  };
  return {
    // user side: start call
    async call(extra = {}) {
      const pc = await init();
      const offer = await pc.createOffer(); await pc.setLocalDescription(offer);
      await new Promise(res => { if (pc.iceGatheringState === 'complete') return res(); const t = setTimeout(res, 2000); pc.addEventListener('icegatheringstatechange', () => { if (pc.iceGatheringState === 'complete') { clearTimeout(t); res(); } }); });
      const r = await axios.post(`${api}/m/calls`, { offer: pc.localDescription.toJSON(), ...extra }, { headers: headers() });
      state.callId = r.data.id; flushLogs(); onStatus?.('ringing'); startPolling();
    },
    // admin side: answer incoming call
    async answer(call) {
      state.callId = call.id;
      const pc = await init();
      await pc.setRemoteDescription(call.offer);
      const ans = await pc.createAnswer(); await pc.setLocalDescription(ans);
      await signal('answer', pc.localDescription.toJSON());
      await drainIce(call.ice || []);
      onStatus?.('active'); startPolling();
    },
    setMuted(m) { state.local?.getAudioTracks().forEach(t => { t.enabled = !m; }); },
    log,
    end,
  };
};

// Attach remote stream to <audio>; returns false if autoplay was blocked (caller should show a tap-to-play button)
export const playRemote = async (el, stream, log) => {
  if (!el) return false;
  el.srcObject = stream; el.muted = false; el.volume = 1;
  try { await el.play(); log?.('audio_play_ok'); return true; } catch (e) { log?.('audio_play_blocked ' + e.name); return false; }
};

// Loudspeaker toggle: iOS via navigator.audioSession (play-and-record = earpiece, playback = speaker); others via setSinkId
export const setSpeaker = async (audioEl, on) => {
  if (!audioEl) return false;
  audioEl.volume = 1;
  if (navigator.audioSession) { try { navigator.audioSession.type = on ? 'playback' : 'play-and-record'; return true; } catch {} }
  if (typeof audioEl.setSinkId !== 'function') return false;
  try {
    const devs = (await navigator.mediaDevices.enumerateDevices()).filter(d => d.kind === 'audiooutput');
    const spk = devs.find(d => /speaker|مكبر/i.test(d.label));
    const ear = devs.find(d => /earpiece|receiver|communications|سماعة/i.test(d.label) || d.deviceId === 'communications');
    const target = on ? (spk || devs.find(d => d.deviceId === 'default') || devs[0]) : (ear || devs.find(d => d.deviceId === 'default'));
    if (target) { await audioEl.setSinkId(target.deviceId === 'default' ? '' : target.deviceId); return true; }
  } catch {}
  return false;
};
